// WaveTalk web client — the embedded mini-app OVERLAY (browser glue). Owns the
// `#miniapp-layer` DOM: a trusted capsule chrome (app title + status + a close
// button the shell controls — a mini-app can neither draw nor remove it), a
// sandboxed content viewport the mini-app's worker renderer paints into, and a
// toast used by trusted capability handlers (share/notify) to present UI on the
// mini-app's behalf. The chrome itself is rendered through the SAME Joana render
// layer (catalog → DomRenderer) — even the mini-app frame is Joana, not raw HTML.

import { hostTranslator } from "../joana/i18n/index.ts";
import type { VNode } from "../joana/render/catalog.ts";
import { DomRenderer, type DomDocument, type DomElement } from "../joana/render/dom-renderer.ts";
import type { RenderEvent } from "../joana/render/patch.ts";
import { applyFrame } from "../joana/render/renderer.ts";
import { ViewHost } from "../joana/render/view-host.ts";
import type { MiniAppStatus } from "../joana/shell/store.ts";

export class MiniAppOverlay {
  /** The viewport the mini-app's worker renderer paints into. */
  readonly contentEl: HTMLElement;
  private readonly chromeEl: HTMLElement;
  private readonly toastEl: HTMLElement;
  private readonly chromeHost = new ViewHost();
  private readonly chromeRenderer: DomRenderer;
  private title = "";
  private status: MiniAppStatus = "idle";
  private error: string | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly layer: HTMLElement,
    private readonly onClose: () => void,
  ) {
    this.chromeEl = document.createElement("div");
    this.chromeEl.className = "ma-chrome";
    this.contentEl = document.createElement("div");
    this.contentEl.className = "ma-content";
    this.toastEl = document.createElement("div");
    this.toastEl.className = "ma-toast";
    layer.append(this.chromeEl, this.contentEl, this.toastEl);

    this.chromeRenderer = new DomRenderer(
      document as unknown as DomDocument,
      this.chromeEl as unknown as DomElement,
      (e) => this.onChromeEvent(e),
    );
    layer.style.display = "none";
  }

  /** Show the overlay for `title`, clearing any previous app's painted content. */
  show(title: string): void {
    this.title = title;
    this.status = "loading";
    this.error = null;
    this.clearContent();
    this.layer.style.display = "flex";
    this.renderChrome();
  }

  hide(): void {
    this.layer.style.display = "none";
    this.clearContent();
  }

  setStatus(status: MiniAppStatus, error: string | null): void {
    this.status = status;
    this.error = error;
    this.renderChrome();
  }

  /** Present a transient message — used by trusted capability handlers so the
   *  SHELL (not the mini-app) draws share/notification UI. */
  toast(message: string): void {
    this.toastEl.textContent = message;
    this.toastEl.classList.add("show");
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toastEl.classList.remove("show"), 2200);
  }

  // The old app's painted nodes must go before a new app paints into the same
  // viewport (the host creates a fresh content renderer per open).
  private clearContent(): void {
    this.contentEl.replaceChildren();
  }

  private renderChrome(): void {
    applyFrame(this.chromeRenderer, this.chromeHost.render(this.chromeTree()));
  }

  private chromeTree(): VNode {
    const t = hostTranslator();
    const subtitle =
      this.status === "loading" ? t("Loading…") : this.status === "error" ? (this.error ?? t("Failed to load")) : t("Mini-app");
    return {
      type: "stack",
      id: "ma-capsule",
      style: {
        direction: "row",
        align: "center",
        gap: 10,
        padding: "12 14",
        background: "brand.gradient.start",
      },
      children: [
        {
          type: "stack",
          id: "ma-titles",
          style: { direction: "column", flex: 1, gap: 1 },
          children: [
            { type: "text", id: "ma-title", props: { value: this.title }, style: { font_size: 17, font_weight: 700, color: "#FFFFFF" } },
            { type: "text", id: "ma-sub", props: { value: subtitle }, style: { font_size: 12, color: this.status === "error" ? "#FFD7D0" : "#FFE9DD" } },
          ],
        },
        {
          type: "button",
          id: "ma-close",
          props: { label: t("✕ Close") },
          style: { background: "rgba(255,255,255,0.18)", color: "#FFFFFF", border: "none", font_size: 14, font_weight: 600, padding: "6 12", radius: 8 },
          action: { intent: "shell", payload: { cmd: "close" } },
        },
      ],
    };
  }

  private onChromeEvent(e: RenderEvent): void {
    const action = this.chromeHost.actionFor(e);
    if (action?.intent === "shell" && (action.payload as { cmd?: string })?.cmd === "close") {
      this.onClose();
    }
  }
}
